import {useEffect, useState} from 'react';

import {isInaccessibleInfo} from 'auth/RequestAccess';
import {TagSchema} from 'constants/RegistryConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import clampEntryName from 'notebook/entry-templates/clampEntryName';
import {
  CreateEntryFromTemplateQuery,
  CreateEntryFromTemplateQueryVariables,
} from 'notebook/entry-templates/CreateEntryFromTemplateModal/__generated/CreateEntryFromTemplateQuery';
import {SCHEMA_AND_TAGS_FROM_TEMPLATE_QUERY} from 'notebook/entry-templates/CreateEntryFromTemplateModal/CreateEntryFromTemplateModal';
import {SelectTemplateCollectionItemTreeTemplate} from 'notebook/entry-templates/CreateEntryFromTemplateModal/utils';
import {FromServerTemplate} from 'notebook/entry-templates/TemplateConstants';
import TemplateDAO from 'notebook/entry-templates/TemplateDAO';
import * as TagUtil from 'schemas/tags/TagUtil';

type CreateEntryFromTemplateQuery_template_InaccessibleInfoNode = ExtractByTypename<
  CreateEntryFromTemplateQuery['template'],
  'InaccessibleInfoNode'
>;

/**
 * Custom hook to consolidate template-related stateful logic for easier reuse. Because templates can set the
 * entry's schema, there's schema-related logic in here as well.
 *
 * The updateTagSchema argument is intended to be the updateTagSchema returned from useTagTableState.
 */
export function useEntryTemplate(
  initialTemplateId: string | null,
  setEntryName: (name: string) => void,
  updateTagSchema: (newTagSchema: TagSchema | null, initialTags?: Array<SchemaTag>) => void
): {
  templateId: string | null;
  changeTemplate: (template: SelectTemplateCollectionItemTreeTemplate | null) => void;
  templateResult: SkippableBenchlingQueryResult<CreateEntryFromTemplateQuery>;
  canChangeSchema: () => boolean;
} {
  const [templateId, setTemplateId] = useState<string | null>(initialTemplateId);

  useEffect(() => {
    if (initialTemplateId) {
      TemplateDAO.get(initialTemplateId).done((template: FromServerTemplate) => {
        setTemplateId(template.id);
        setEntryName(clampEntryName(template.name));
      });
    }
  }, [initialTemplateId, setEntryName]);

  const result = useSkippableBenchlingQuery<
    CreateEntryFromTemplateQuery,
    CreateEntryFromTemplateQueryVariables
  >(SCHEMA_AND_TAGS_FROM_TEMPLATE_QUERY, {
    variables: {id: templateId!},
    fetchPolicy: 'cache-and-network',
    skip: templateId == null,
  });

  const changeTemplate = (template: SelectTemplateCollectionItemTreeTemplate | null): void => {
    if (template) {
      setTemplateId(template.id);
      setEntryName(clampEntryName(template.name));
    } else {
      setTemplateId(null);
      setEntryName('');
    }
  };

  // Schema cannot be changed by the user if it was set on the template
  const canChangeSchema = (): boolean => {
    if (result.type === QueryResultType.SUCCESS && !isTemplateInaccessibleInfo(result.data.template)) {
      return !result.data.template.tagSchema;
    }
    return true;
  };

  const _data: CreateEntryFromTemplateQuery | null =
    result.type === QueryResultType.SUCCESS && !isInaccessibleInfo(result.data) ? result.data : null;

  useEffect(() => {
    if (result.type === QueryResultType.SUCCESS && _data) {
      const template = _data.template;
      if (!isTemplateInaccessibleInfo(template) && template.tagSchema != null) {
        const _tagSchema = graphQLToREST('TagSchema', template.tagSchema);
        // Custom tags are automatically copied by the backend, we use this helper to extract out only the schema tags
        const {schemaTags} = TagUtil.getSortedSchemaAndCustomTags({
          tags: template.tagsJson,
          tagSchema: _tagSchema,
        });
        updateTagSchema(_tagSchema, schemaTags);
      } else {
        updateTagSchema(null);
      }
    }
  }, [result.type, _data, updateTagSchema]);

  return {templateId, changeTemplate, templateResult: result, canChangeSchema};
}

function isTemplateInaccessibleInfo(
  info: object
): info is CreateEntryFromTemplateQuery_template_InaccessibleInfoNode {
  return 'invalidId' in info;
}
